import { sameRoofPlane } from '@payaca/helpers/solarRoofPlaneHelper';
import {
  getKkValue,
  orientationFromSouth,
  resolvePostcodeToZone,
} from '@payaca/helpers/solarMcsLookup';
import { type WorkingFace } from './types';

type TracedFace = {
  polygon: {
    outer: ReadonlyArray<{ lat: number; lng: number }>;
    holes: ReadonlyArray<ReadonlyArray<{ lat: number; lng: number }>>;
  };
  pitchDegrees: number;
  azimuthDegrees: number;
};

/**
 * The MIS 3002 yield of one panel on each WORKING face, by face id.
 *
 * The per-panel MCS figure the server stored was worked out for the traced
 * face at its traced pitch, orientation and outline (the zone's Kk at that
 * pitch and orientation, times the shade factor read off a horizon around
 * that outline). A working face keeps that figure only while it is still
 * that plane. Edited or new UK faces use the same irradiance table as the
 * estimate snapshot, with shading unassessed (factor 1). Callers must make
 * that missing assessment visible, because the previous roof's shading
 * cannot be carried onto a different plane.
 */
export const mcsPerPanelByWorkingFace = (args: {
  workingFaces: ReadonlyArray<WorkingFace>;
  tracedFaces: ReadonlyArray<TracedFace> | null | undefined;
  /** The stored per-panel figure by TRACED face index. */
  mcsPerPanelByTracedFace: ReadonlyMap<number, number>;
  /** UK site postcode, null outside the UK or when unknown. */
  postcode?: string | null;
  capacityWatts?: number | null;
}): Map<number, number> => {
  const byWorkingFace = new Map<number, number>();
  const zone = args.postcode ? resolvePostcodeToZone(args.postcode) : null;
  for (const face of args.workingFaces) {
    const traced = args.tracedFaces?.[face.id];
    const stored = args.mcsPerPanelByTracedFace.get(face.id);
    const samePlane = !face.isNew && traced && sameRoofPlane(face, traced);
    if (samePlane) {
      // Old rows with no per-panel yield may still hold a provider shade
      // assessment. Regenerate those rows rather than inventing SF=1.
      if (stored != null) byWorkingFace.set(face.id, stored);
      continue;
    }
    if (!zone || args.capacityWatts == null) continue;
    const kk = getKkValue(
      zone,
      face.pitchDegrees,
      orientationFromSouth(face.azimuthDegrees)
    );
    if (kk != null) {
      byWorkingFace.set(face.id, (args.capacityWatts / 1000) * kk);
    }
  }
  return byWorkingFace;
};
